import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { type ScheduleRequestSource } from '@frontend/schedule-helpers';
import { PopoverMenuItem, Text, formatDate, useModalControl } from '@frontend/design-system';
import { StatusBadge } from '../../../../../../../components/status-badge';
import { useScheduleSettingsSmallMode } from '../../../../../../../hooks/settings/use-schedule-settings-small-mode';
import { useCheckCanSendMessages } from '../../hooks/use-check-can-send-messages';
import type { ScheduleRequestActionCalledFrom } from '../../hooks/use-schedule-request-actions';
import { useScheduleRequestItemHeaderController } from '../../hooks/use-schedule-request-item-header-controller';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import {
  type ScheduleListItemVariant,
  ScheduleRequestItemHeaderUI,
} from '../components/schedule-request-item-header-ui';

type Props = {
  isAiRequest?: boolean;
  isUrgentRequest?: boolean;
  requestVersion: string;
  name: string;
  appointmentTime: string;
  personPhone: string;
  locationId: string;
  scheduleRequestId: string;
  variant?: ScheduleListItemVariant;
  patientContactedAt?: string;
  locationChip?: (locationName: string) => React.ReactNode;
  requestSource: ScheduleRequestSource;
  deleteRequestModalProps: ReturnType<typeof useModalControl>;
  dismissRequestModalProps: ReturnType<typeof useModalControl>;
  onCompleteRequest: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onDismissRequestIntent: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onMessage: (updateStatus: boolean, actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onPhoneCall: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  isV3Request: boolean;
};

export const PreferenceBasedScheduleRequestItemHeader = ({
  isAiRequest = false,
  isUrgentRequest = false,
  requestVersion,
  name,
  appointmentTime,
  personPhone,
  locationId,
  scheduleRequestId,
  variant,
  locationChip,
  patientContactedAt,
  deleteRequestModalProps,
  dismissRequestModalProps,
  onCompleteRequest,
  onDismissRequestIntent,
  onMessage,
  onPhoneCall,
  requestSource,
  isV3Request,
}: Props) => {
  const { t } = useTranslation('schedule-request');
  const { isSmallMode } = useScheduleSettingsSmallMode();
  const canSendMessages = useCheckCanSendMessages(locationId);
  const showIconOnly = isSmallMode || variant !== 'widget';

  const {
    locationName,
    appointmentTimeLabel,
    getMenuProps,
    getTriggerProps,
    handleViewRequestMenuItemClick,
    handleMessageMenuItemClick,
    handleCallMenuItemClick,
    handleDeleteMenuItemClick,
    handleDismissMenuItemClick,
    handleDoneMenuItemClick,
  } = useScheduleRequestItemHeaderController({
    requestVersion,
    locationId,
    scheduleRequestId,
    appointmentTime,
    variant,
    requestSource,
    deleteRequestModalProps,
    dismissRequestModalProps,
    onCompleteRequest,
    onDismissRequestIntent,
    onMessage,
    onPhoneCall,
  });

  const isPatientContacted = !!patientContactedAt;

  const primaryMeta = (
    <>
      {isAiRequest && <StatusBadge variant='ai-requested' iconOnly={showIconOnly} />}
      {isUrgentRequest && <StatusBadge variant='urgent' />}
    </>
  );

  const secondaryMeta = (
    <>
      <Text textAlign='left' size='small'>
        {appointmentTimeLabel}
      </Text>
      {isPatientContacted && (
        <Text textAlign='left' size='small' color={'subdued'}>
          {t('Last Contacted on {{datetime}}', {
            datetime: formatDate(patientContactedAt, 'MMM D, YYYY, h:mm A'),
          })}
        </Text>
      )}
    </>
  );

  const menuItems = (
    <>
      {variant === 'widget' && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemViewRequestBtn}
          Icon={() => <Icon name='lists-small' />}
          onClick={handleViewRequestMenuItemClick}
        >
          {t('View Request')}
        </PopoverMenuItem>
      )}
      {personPhone && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemMessageBtn}
          Icon={() => <Icon name='message-small' />}
          disabled={!canSendMessages}
          hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
          onClick={handleMessageMenuItemClick}
        >
          {t('Message')}
        </PopoverMenuItem>
      )}
      {personPhone && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemCallBtn}
          Icon={() => <Icon name='phone-small' />}
          onClick={handleCallMenuItemClick}
        >
          {t('Call')}
        </PopoverMenuItem>
      )}
      {isPatientContacted && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDoneBtn}
          Icon={() => <Icon name='check-small' />}
          onClick={handleDoneMenuItemClick}
        >
          {t('Mark as Done')}
        </PopoverMenuItem>
      )}
      {(!isPatientContacted || !isV3Request) && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDismissBtn}
          Icon={() => <Icon name='remove2-small' />}
          onClick={handleDismissMenuItemClick}
        >
          {t('Dismiss')}
        </PopoverMenuItem>
      )}
      {isV3Request && (
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDeleteBtn}
          Icon={() => <Icon name='trash-small' />}
          onClick={handleDeleteMenuItemClick}
        >
          {t('Delete')}
        </PopoverMenuItem>
      )}
    </>
  );

  return (
    <ScheduleRequestItemHeaderUI
      locationName={locationName}
      name={name}
      primaryMeta={primaryMeta}
      secondaryMeta={secondaryMeta}
      menuItems={menuItems}
      locationChip={locationChip}
      getTriggerProps={getTriggerProps}
      getMenuProps={getMenuProps}
      variant={variant}
    />
  );
};
